import { useCallback, useEffect, useRef } from 'react';

type PauseHandler = () => void;

export interface ExclusivePlayback {
  /** Pauses every other registered player and marks this one as the active player. */
  claim: () => void;
}

// Module-level registry shared by every player on the page, so only one video plays at a time.
const registry = new Set<PauseHandler>();

/** Clears the page-wide registry. Exported for tests, which reuse the same module instance. */
export function resetExclusivePlaybackRegistry(): void {
  registry.clear();
}

/**
 * Joins the page-wide "one video at a time" registry.
 *
 * A disabled player takes no part in it at all: it neither pauses the others nor is paused by
 * them, which is what an ambient background video needs.
 */
export function useExclusivePlayback(pause: PauseHandler, isEnabled = true): ExclusivePlayback {
  const pauseRef = useRef(pause);
  const handlerRef = useRef<PauseHandler | null>(null);

  handlerRef.current ??= () => pauseRef.current();

  useEffect(() => {
    pauseRef.current = pause;
  }, [pause]);

  useEffect(() => {
    const handler = handlerRef.current;
    if (!handler || !isEnabled) {
      return undefined;
    }

    registry.add(handler);
    return () => {
      registry.delete(handler);
    };
  }, [isEnabled]);

  const claim = useCallback(() => {
    if (!isEnabled) {
      return;
    }

    const handler = handlerRef.current;
    registry.forEach((registered) => {
      if (registered !== handler) {
        registered();
      }
    });
  }, [isEnabled]);

  return { claim };
}
